'use client'

import { tw } from '@/lib/utils'

import { fillLabel } from './fill-label'
import { StarIcon } from './star-icon'

export interface RatingDistributionLabels {
  /** "1 star" */
  starOne: string
  /** "{{count}} stars" */
  starOther: string
  /** "1 review" */
  reviewCountOne: string
  /** "{{count}} reviews" */
  reviewCountOther: string
}

interface RatingDistributionProps {
  /** Review counts by whole star, index 0 = 1 star … index 4 = 5 stars. */
  distribution: number[]
  totalCount: number
  labels: RatingDistributionLabels
  className?: string
}

const ROWS = [5, 4, 3, 2, 1]

/** How the reviews spread across the five star ratings — the same bar grammar
 *  as the scorecard's sub-scores, so a lopsided or thin sample shows at a
 *  glance instead of hiding behind one average. */
export const RatingDistribution = ({ distribution, totalCount, labels, className }: RatingDistributionProps) => (
  <ul className={tw('space-y-2', className)}>
    {ROWS.map((stars) => {
      const count = distribution[stars - 1] ?? 0
      const share = totalCount > 0 ? (count / totalCount) * 100 : 0
      const starsText = stars === 1 ? labels.starOne : fillLabel(labels.starOther, { count: stars })
      const countText = count === 1 ? labels.reviewCountOne : fillLabel(labels.reviewCountOther, { count })
      return (
        <li key={stars} className="flex items-center gap-3">
          <span className="sr-only">{`${starsText}: ${countText}`}</span>
          <span
            aria-hidden="true"
            className="text-navy flex w-8 shrink-0 items-center gap-1 text-[14px] font-medium tabular-nums"
          >
            {stars}
            <StarIcon fill="currentColor" size={11} />
          </span>
          <span aria-hidden="true" className="bg-navy-5 h-2 min-w-0 flex-1 overflow-hidden rounded-full">
            <span className="bg-lapis block h-full rounded-full" style={{ width: `${share}%` }} />
          </span>
          <span aria-hidden="true" className="text-navy-80 w-8 shrink-0 text-right text-[14px] tabular-nums">
            {count}
          </span>
        </li>
      )
    })}
  </ul>
)
